import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../utils/translations';
import { 
  Users, 
  Settings, 
  CreditCard, 
  Calendar, 
  Wrench, 
  CheckCircle,
  ArrowRight,
  Star,
  Clock,
  Shield,
  Zap,
  Target,
  Award,
  Brain,
  Code2
} from 'lucide-react';

const Services = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const [activeService, setActiveService] = useState('consulting');

  const serviceIcons = {
    consulting: <Users className="w-8 h-8" />,
    installation: <Settings className="w-8 h-8" />,
    implementation: <CreditCard className="w-8 h-8" />,
    subscription: <Calendar className="w-8 h-8" />,
    tools: <Wrench className="w-8 h-8" />,
    customDev: <Code2 className="w-8 h-8" />
  };

  const services = {
    consulting: {
      title: language === 'en' ? 'Expert Consulting' : 'Consultoría Experta',
      subtitle: language === 'en' ? 'Strategic guidance for your digital transformation' : 'Orientación estratégica para tu transformación digital',
      description: language === 'en' 
        ? 'Our certified consultants analyze your business processes and design the optimal solution architecture. We provide strategic recommendations to maximize ROI and ensure successful implementation.'
        : 'Nuestros consultores certificados analizan tus procesos empresariales y diseñan la arquitectura de solución óptima. Proporcionamos recomendaciones estratégicas para maximizar el ROI y asegurar una implementación exitosa.',
      features: [
        language === 'en' ? 'Business Process Analysis' : 'Análisis de Procesos Empresariales',
        language === 'en' ? 'Solution Architecture Design' : 'Diseño de Arquitectura de Solución',
        language === 'en' ? 'ROI Assessment & Planning' : 'Evaluación y Planificación de ROI',
        language === 'en' ? 'Change Management Strategy' : 'Estrategia de Gestión del Cambio',
        language === 'en' ? 'Best Practices Implementation' : 'Implementación de Mejores Prácticas'
      ],
      benefits: [
        { icon: <Target className="w-5 h-5" />, text: language === 'en' ? 'Reduced implementation time by 40%' : 'Tiempo de implementación reducido en 40%' },
        { icon: <Award className="w-5 h-5" />, text: language === 'en' ? 'Industry-specific expertise' : 'Experiencia específica por industria' },
        { icon: <Shield className="w-5 h-5" />, text: language === 'en' ? 'Risk mitigation strategies' : 'Estrategias de mitigación de riesgos' }
      ],
      imageSrc: 'https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1'
    },
    installation: {
      title: language === 'en' ? 'Installation & Certification' : 'Instalación y Certificación',
      subtitle: language === 'en' ? 'Professional setup with quality assurance' : 'Configuración profesional con garantía de calidad',
      description: language === 'en'
        ? 'Complete system installation and configuration by certified technicians. We ensure your LAIMU Trantor platform is properly set up, tested, and certified for optimal performance.'
        : 'Instalación y configuración completa del sistema por técnicos certificados. Aseguramos que tu plataforma LAIMU Trantor esté correctamente configurada, probada y certificada para un rendimiento óptimo.',
      features: [
        language === 'en' ? 'System Installation & Configuration' : 'Instalación y Configuración del Sistema',
        language === 'en' ? 'Quality Assurance Testing' : 'Pruebas de Garantía de Calidad',
        language === 'en' ? 'Performance Optimization' : 'Optimización de Rendimiento',
        language === 'en' ? 'Security Configuration' : 'Configuración de Seguridad',
        language === 'en' ? 'Official Certification' : 'Certificación Oficial'
      ],
      benefits: [
        { icon: <Zap className="w-5 h-5" />, text: language === 'en' ? '99.9% system uptime guarantee' : 'Garantía de 99.9% de tiempo activo' },
        { icon: <Shield className="w-5 h-5" />, text: language === 'en' ? 'Enterprise-grade security' : 'Seguridad de nivel empresarial' },
        { icon: <CheckCircle className="w-5 h-5" />, text: language === 'en' ? 'Certified installation process' : 'Proceso de instalación certificado' }
      ],
      imageSrc: 'https://images.pexels.com/photos/3861969/pexels-photo-3861969.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1'
    },
    implementation: {
      title: language === 'en' ? 'One-Time Implementation Fee' : 'Cuota Única de Implementación',
      subtitle: language === 'en' ? 'Transparent, fixed-cost setup' : 'Configuración transparente de costo fijo',
      description: language === 'en'
        ? 'Single upfront payment covering complete system implementation. No hidden costs, no surprises - just a comprehensive setup that gets you operational quickly.'
        : 'Pago único inicial que cubre la implementación completa del sistema. Sin costos ocultos, sin sorpresas - solo una configuración integral que te pone operativo rápidamente.',
      features: [
        language === 'en' ? 'Complete System Setup' : 'Configuración Completa del Sistema',
        language === 'en' ? 'Data Migration Services' : 'Servicios de Migración de Datos',
        language === 'en' ? 'User Account Creation' : 'Creación de Cuentas de Usuario',
        language === 'en' ? 'Initial Training Session' : 'Sesión de Entrenamiento Inicial',
        language === 'en' ? 'Go-Live Support' : 'Soporte de Puesta en Marcha'
      ],
      benefits: [
        { icon: <CreditCard className="w-5 h-5" />, text: language === 'en' ? 'No hidden fees or surprises' : 'Sin tarifas ocultas o sorpresas' },
        { icon: <Clock className="w-5 h-5" />, text: language === 'en' ? 'Fast 2-week implementation' : 'Implementación rápida en 2 semanas' },
        { icon: <Star className="w-5 h-5" />, text: language === 'en' ? 'Fixed-price guarantee' : 'Garantía de precio fijo' }
      ],
      imageSrc: 'https://images.pexels.com/photos/6801648/pexels-photo-6801648.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1'
    },
    subscription: {
      title: language === 'en' ? 'Monthly Subscription' : 'Suscripción Mensual',
      subtitle: language === 'en' ? 'Flexible, scalable pricing model' : 'Modelo de precios flexible y escalable',
      description: language === 'en'
        ? 'Affordable monthly subscription that grows with your business. Includes all platform features, regular updates, technical support, and cloud hosting.'
        : 'Suscripción mensual asequible que crece con tu negocio. Incluye todas las características de la plataforma, actualizaciones regulares, soporte técnico y hosting en la nube.',
      features: [
        language === 'en' ? 'All Platform Modules Included' : 'Todos los Módulos de la Plataforma Incluidos',
        language === 'en' ? 'Regular Software Updates' : 'Actualizaciones Regulares de Software',
        language === 'en' ? '24/7 Technical Support' : 'Soporte Técnico 24/7',
        language === 'en' ? 'Cloud Hosting & Backup' : 'Hosting en la Nube y Respaldo',
        language === 'en' ? 'Scalable User Licenses' : 'Licencias de Usuario Escalables'
      ],
      benefits: [
        { icon: <Calendar className="w-5 h-5" />, text: language === 'en' ? 'No long-term contracts' : 'Sin contratos a largo plazo' },
        { icon: <Zap className="w-5 h-5" />, text: language === 'en' ? 'Instant feature access' : 'Acceso instantáneo a características' },
        { icon: <Shield className="w-5 h-5" />, text: language === 'en' ? 'Automatic backups included' : 'Respaldos automáticos incluidos' }
      ],
      imageSrc: 'https://images.pexels.com/photos/6801874/pexels-photo-6801874.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1'
    },
    tools: {
      title: language === 'en' ? 'Additional Tools & Modules' : 'Herramientas y Módulos Adicionales',
      subtitle: language === 'en' ? 'Extend functionality with specialized tools' : 'Extiende la funcionalidad con herramientas especializadas',
      description: language === 'en'
        ? 'Enhance your LAIMU Trantor platform with specialized modules and tools designed for specific industry needs and advanced functionality requirements.'
        : 'Mejora tu plataforma LAIMU Trantor con módulos y herramientas especializadas diseñadas para necesidades específicas de la industria y requerimientos de funcionalidad avanzada.',
      features: [
        language === 'en' ? 'Industry-Specific Modules' : 'Módulos Específicos por Industria',
        language === 'en' ? 'Advanced Analytics Tools' : 'Herramientas de Análisis Avanzado',
        language === 'en' ? 'API Integration Modules' : 'Módulos de Integración API',
        language === 'en' ? 'Custom Workflow Builder' : 'Constructor de Flujos de Trabajo Personalizados',
        language === 'en' ? 'Mobile Applications' : 'Aplicaciones Móviles'
      ],
      benefits: [
        { icon: <Wrench className="w-5 h-5" />, text: language === 'en' ? 'Tailored to your industry' : 'Adaptado a tu industria' },
        { icon: <Zap className="w-5 h-5" />, text: language === 'en' ? 'Plug-and-play integration' : 'Integración plug-and-play' },
        { icon: <Target className="w-5 h-5" />, text: language === 'en' ? 'Specialized functionality' : 'Funcionalidad especializada' }
      ],
      imageSrc: 'https://images.pexels.com/photos/3861958/pexels-photo-3861958.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1'
    },
    customDev: {
      title: language === 'en' ? 'Custom Development & AI' : 'Desarrollo a la Medida e IA',
      subtitle: language === 'en' ? 'Bespoke software and AI-assisted innovation' : 'Software personalizado e innovación asistida por IA',
      description: language === 'en'
        ? 'Unlock your business potential with tailor-made software solutions integrated with AI-driven workflows. We design, build, and deploy custom web applications, mobile apps, and intelligent dashboards that automate complex operations, boost productivity, and provide strategic business analytics.'
        : 'Desbloquea el potencial de tu negocio con soluciones de software a la medida integradas con flujos de trabajo basados en Inteligencia Artificial. Diseñamos, construimos y desplegamos aplicaciones web personalizadas, apps móviles y tableros inteligentes que automatizan operaciones complejas, impulsan la productividad y brindan analítica de negocio estratégica.',
      features: [
        language === 'en' ? 'Bespoke Web & Mobile App Development' : 'Desarrollo de Software Web y Apps Móviles a la Medida',
        language === 'en' ? 'AI-Agentic Workflows & Chatbots Integration' : 'Integración de Agentes y Chatbots con IA',
        language === 'en' ? 'Intelligent Business Dashboards (KPIs)' : 'Tableros de Negocio Inteligentes y KPIs Avanzados',
        language === 'en' ? 'System Integrations & Custom APIs' : 'Integraciones de Sistemas y APIs a la Medida',
        language === 'en' ? 'Legacy System Modernization' : 'Modernización de Sistemas Heredados (Legacy)'
      ],
      benefits: [
        { icon: <Brain className="w-5 h-5" />, text: language === 'en' ? 'AI-assisted intelligence & audits' : 'Auditorías e inteligencia asistidas por IA' },
        { icon: <Zap className="w-5 h-5" />, text: language === 'en' ? 'Bespoke fit to your exact workflows' : 'Adaptación exacta a tus flujos de trabajo' },
        { icon: <Shield className="w-5 h-5" />, text: language === 'en' ? 'Secure, scalable cloud-native code' : 'Código seguro, escalable y nativo de la nube' }
      ],
      imageSrc: '/custom_doctorez.png'
    }
  };

  return (
    <section id="services" className="py-16 md:py-24 bg-gradient-to-br from-gray-50 to-white">
      <div className="container mx-auto px-4 md:px-6">
        {/* Header Section */}
        <div className="text-center max-w-4xl mx-auto mb-16">
          <div className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium mb-6" style={{ backgroundColor: '#e0f2fe', color: '#115d7b' }}>
            <Star className="w-4 h-4 mr-2" />
            {language === 'en' ? 'Complete Service Portfolio' : 'Portafolio Completo de Servicios'}
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            {language === 'en' ? 'Our Professional Services' : 'Nuestros Servicios Profesionales'}
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed">
            {language === 'en' 
              ? 'From consultation to implementation, we provide comprehensive services to ensure your success with LAIMU Trantor. Our expert team guides you through every step of your digital transformation journey.'
              : 'Desde consultoría hasta implementación, proporcionamos servicios integrales para asegurar tu éxito con LAIMU Trantor. Nuestro equipo experto te guía a través de cada paso de tu viaje de transformación digital.'
            }
          </p>
        </div>

        {/* Services Navigation */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {Object.entries(services).map(([key, service]) => (
            <button
              key={key}
              onClick={() => setActiveService(key)}
              className={`flex items-center px-6 py-3 rounded-full transition-all duration-300 font-medium ${
                activeService === key 
                  ? 'text-white shadow-lg transform scale-105' 
                  : 'bg-white text-gray-700 hover:bg-gray-50 shadow-md hover:shadow-lg'
              }`}
              style={activeService === key ? { backgroundColor: '#115d7b' } : {}}
            >
              <span className="mr-2">
                {serviceIcons[key as keyof typeof serviceIcons]}
              </span>
              <span>{service.title}</span>
            </button>
          ))}
        </div>

        {/* Active Service Display */}
        <div className="max-w-7xl mx-auto">
          {Object.entries(services).map(([key, service]) => (
            <div 
              key={key}
              className={`${activeService === key ? 'block' : 'hidden'} transition-all duration-500`}
            >
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                <div className="grid lg:grid-cols-2 gap-0">
                  {/* Content Side */}
                  <div className="p-8 md:p-12 lg:p-16">
                    <div className="mb-8">
                      <div className="flex items-center mb-4">
                        <div className="w-16 h-16 rounded-full flex items-center justify-center mr-4" style={{ backgroundColor: '#e0f2fe' }}>
                          <span style={{ color: '#115d7b' }}>
                            {serviceIcons[key as keyof typeof serviceIcons]}
                          </span>
                        </div>
                        <div>
                          <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                            {service.title}
                          </h3>
                          <p className="text-lg font-medium" style={{ color: '#115d7b' }}>
                            {service.subtitle}
                          </p>
                        </div>
                      </div>
                      <p className="text-gray-600 text-lg leading-relaxed">
                        {service.description}
                      </p>
                    </div>

                    {/* Features */}
                    <div className="mb-8">
                      <h4 className="text-xl font-bold text-gray-900 mb-4">
                        {language === 'en' ? 'What\'s Included:' : 'Qué Incluye:'}
                      </h4>
                      <div className="space-y-3">
                        {service.features.map((feature, index) => (
                          <div key={index} className="flex items-center">
                            <CheckCircle className="w-5 h-5 mr-3 flex-shrink-0" style={{ color: '#10b981' }} />
                            <span className="text-gray-700">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Benefits */}
                    <div className="mb-8">
                      <h4 className="text-xl font-bold text-gray-900 mb-4">
                        {language === 'en' ? 'Key Benefits:' : 'Beneficios Clave:'}
                      </h4>
                      <div className="space-y-3">
                        {service.benefits.map((benefit, index) => (
                          <div key={index} className="flex items-center p-3 rounded-lg bg-gray-50">
                            <span className="mr-3" style={{ color: '#115d7b' }}>
                              {benefit.icon}
                            </span>
                            <span className="text-gray-700 font-medium">{benefit.text}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* CTA Buttons */}
                    <div className="flex flex-col sm:flex-row gap-4">
                      <a
                        href="/demo"
                        className="px-8 py-4 text-white rounded-lg transition-colors font-bold text-lg flex items-center justify-center shadow-lg"
                        style={{ backgroundColor: '#115d7b' }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#0f5269'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#115d7b'}
                      >
                        {language === 'en' ? 'Get Started' : 'Comenzar'}
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </a>
                      <button
                        onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                        className="px-8 py-4 border-2 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-bold text-lg"
                        style={{ borderColor: '#115d7b' }}
                      >
                        {language === 'en' ? 'Learn More' : 'Saber Más'}
                      </button>
                    </div>
                  </div>

                  {/* Image Side */}
                  <div className="relative overflow-hidden flex items-center justify-center bg-gray-50">
                    {service.imageSrc.startsWith('/') ? (
                      <div className="w-full h-full p-6 md:p-12 flex items-center justify-center">
                        <img 
                          src={service.imageSrc}
                          alt={service.title}
                          className="max-w-full max-h-[350px] lg:max-h-[450px] object-contain rounded-xl shadow-2xl border border-gray-250/50"
                        />
                      </div>
                    ) : (
                      <>
                        <img 
                          src={service.imageSrc}
                          alt={service.title}
                          className="w-full h-full object-cover min-h-[500px] lg:min-h-[600px]"
                        />
                        <div className="absolute inset-0 bg-gradient-to-l from-transparent via-transparent to-black/10"></div>
                      </>
                    )}
                    
                    {/* Floating Stats Card */}
                    <div className="absolute bottom-8 left-8 right-8 bg-white/95 backdrop-blur-sm rounded-xl p-6 shadow-lg">
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div>
                          <div className="text-2xl font-bold" style={{ color: '#115d7b' }}>500+</div>
                          <div className="text-gray-600 text-sm">
                            {language === 'en' ? 'Clients Served' : 'Clientes Atendidos'}
                          </div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold" style={{ color: '#ff8c00' }}>98%</div>
                          <div className="text-gray-600 text-sm">
                            {language === 'en' ? 'Success Rate' : 'Tasa de Éxito'}
                          </div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold" style={{ color: '#10b981' }}>24/7</div>
                          <div className="text-gray-600 text-sm">
                            {language === 'en' ? 'Support' : 'Soporte'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA Section */}
        <div className="mt-20 text-center">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-8 md:p-12 rounded-2xl border border-blue-100">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">
              {language === 'en' 
                ? 'Ready to Transform Your Business?' 
                : '¿Listo para Transformar Tu Negocio?'
              }
            </h3>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto text-lg">
              {language === 'en' 
                ? 'Our comprehensive service portfolio ensures your success from day one. Let our experts guide your digital transformation journey.'
                : 'Nuestro portafolio integral de servicios asegura tu éxito desde el primer día. Deja que nuestros expertos guíen tu viaje de transformación digital.'
              }
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="/demo"
                className="px-8 py-4 text-white rounded-lg transition-colors font-bold text-lg shadow-lg"
                style={{ backgroundColor: '#115d7b' }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#0f5269'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#115d7b'}
              >
                {language === 'en' ? 'Schedule Consultation' : 'Agendar Consulta'}
              </a>
              <button 
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                className="px-8 py-4 border-2 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-bold text-lg"
                style={{ borderColor: '#115d7b' }}
              >
                {language === 'en' ? 'Contact Sales' : 'Contactar Ventas'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;